// src/shared/components/ui/AdvancedFilter/components/Combobox.tsx
'use client';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { faCheck, faChevronDown, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@shared/components/ui/tooltip';
import { cn } from '@shared/utils/utils';
import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { ComboboxProps } from '../types';
import { getTextColorForBackground } from '@modules/CRM/List/utils/colorUtils';

/** Texto truncado con "..." y tooltip solo cuando el contenido desborda */
const TruncatedLabelWithTooltip = ({
  text,
  className,
  testId,
}: {
  text: string;
  className?: string;
  testId?: string;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const element = textRef.current;
    if (!element) return;
    setIsTruncated(element.scrollWidth - element.clientWidth > 1);
  }, []);

  useLayoutEffect(() => {
    updateTruncation();

    const element = textRef.current;
    if (!element) return;

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);

    const parent = element.parentElement;
    if (parent) observer.observe(parent);

    return () => observer.disconnect();
  }, [text, updateTruncation]);

  return (
    <Tooltip
      disabled={!isTruncated}
      content={<span className="max-w-xs whitespace-normal break-words text-text">{text}</span>}
      trigger={
        <span ref={textRef} className={cn('block w-full min-w-0 max-w-full truncate', className)}>
          {text}
        </span>
      }
      side="top"
      align="start"
      data-testid={testId ?? 'combobox-truncated-label-tooltip'}
    />
  );
};

/**
 * Componente Combobox estático optimizado con React.memo
 *
 * Selector desplegable para opciones predefinidas con búsqueda integrada.
 * Ideal para listas de opciones limitadas (< 100 elementos).
 *
 * @param options - Array de opciones { label, value }
 * @param placeholder - Texto placeholder
 * @param value - Valor seleccionado actual
 * @param setValue - Función para actualizar el valor
 * @param className - Clases CSS adicionales para el trigger wrapper
 * @param disabled - Si el componente está deshabilitado
 * @param dataTestId - ID para testing
 * @param translations - Textos traducidos opcionales
 */
const Combobox = memo(
  ({
    buttonAriaLabelledBy,
    options,
    placeholder,
    value,
    setValue,
    // Por defecto ocupa todo el ancho del contenedor padre
    className = 'w-full block',
    disabled = false,
    dataTestId,
    translations,
    placeholderClassName,
    buttonClassName,
    onCreateOption,
    createOptionLabel,
    isCreatingOption = false,
    onSearchChange,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    loadMoreLabel,
  }: ComboboxProps) => {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    // Si no llega un dataTestId desde fuera, usamos uno por defecto
    const baseTestId = dataTestId || 'combobox';

    /**
     * Cuando el usuario selecciona un elemento,
     * actualizamos el valor y cerramos el popover
     */
    const handleSelect = useCallback(
      (selectedValue: string) => {
        setValue(selectedValue);
        setOpen(false);
      },
      [setValue],
    );

    const notFoundText = translations?.notFound || 'No encontrado.';
    const trimmedSearchValue = searchValue.trim();
    const showCreateOption = Boolean(onCreateOption);
    const canCreateOption = Boolean(onCreateOption && trimmedSearchValue.length > 0);

    const handleCreateOption = useCallback(() => {
      if (!onCreateOption || !trimmedSearchValue) return;
      onCreateOption(trimmedSearchValue);
      setOpen(false);
      setSearchValue('');
    }, [onCreateOption, trimmedSearchValue]);

    return (
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        {/* El trigger ocupa todo el ancho disponible */}
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-labelledby={buttonAriaLabelledBy}
            className={cn(
              className,
              'bg-card-bg border border-card-border rounded px-2 py-[9px] text-sm flex justify-between items-center cursor-pointer transition-colors',
              'hover:bg-accent/50',
              disabled && 'pointer-events-none opacity-70',
              buttonClassName,
            )}
            data-testid={baseTestId}
            disabled={disabled}
          >
            <span
              className={cn(
                'min-w-0 flex-1 overflow-hidden text-left',
                value ? 'text-foreground' : 'text-muted-foreground',
                !value && placeholderClassName,
              )}
            >
              {value
                ? (() => {
                    const selectedOption = options.find((opt) => opt.value === value);
                    if (!selectedOption) return placeholder;
                    return selectedOption.color ? (
                      <span
                        className="inline-flex max-w-full items-center truncate rounded-full px-3 py-0.5 text-sm"
                        style={{
                          backgroundColor: selectedOption.color,
                          color: getTextColorForBackground(selectedOption.color),
                        }}
                      >
                        {selectedOption.label}
                      </span>
                    ) : (
                      <TruncatedLabelWithTooltip
                        text={selectedOption.label}
                        className="text-left leading-tight"
                        testId={`${baseTestId}-selected-label-tooltip`}
                      />
                    );
                  })()
                : placeholder}
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="ml-2 h-2 w-2 shrink-0 opacity-50 text-muted-foreground" />
          </button>
        </PopoverTrigger>

        {/*
          El contenido del popover tendrá al menos el ancho del trigger,
          usando la variable CSS --radix-popover-trigger-width para min-width
        */}
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onWheel={(e) => {
            // Prevenir que el scroll se propague al contenedor padre
            e.stopPropagation();
          }}
        >
          <Command shouldFilter={!onSearchChange}>
            <CommandInput
              placeholder={placeholder}
              value={searchValue}
              onValueChange={(query) => {
                setSearchValue(query);
                onSearchChange?.(query);
              }}
            />
            <CommandList
              onWheel={(e) => {
                // Prevenir que el scroll se propague al contenedor padre
                e.stopPropagation();
              }}
            >
              <CommandEmpty>{notFoundText}</CommandEmpty>
              <CommandGroup>
                {options.map((option, index) => {
                  const searchValue =
                    (option as { searchValue?: string }).searchValue ||
                    [option.label, option.value, option.description].filter(Boolean).join(' ');

                  return (
                    <CommandItem
                      key={`${option.value}-${index}`}
                      value={searchValue}
                      onSelect={() => handleSelect(option.value)}
                      className="items-start min-w-0"
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className={cn(
                          'mr-2 mt-0.5 h-3.5 w-3.5 shrink-0',
                          value === option.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {option.color ? (
                        <span
                          className="inline-flex items-center rounded-full px-3 py-0.5 text-sm"
                          style={{
                            backgroundColor: option.color,
                            color: getTextColorForBackground(option.color),
                          }}
                        >
                          {option.label}
                        </span>
                      ) : option.description ? (
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5 text-left">
                          <TruncatedLabelWithTooltip
                            text={option.label}
                            className="text-sm text-foreground"
                            testId={`${baseTestId}-option-${index}-tooltip`}
                          />
                          <span className="text-xs leading-snug text-muted-foreground">{option.description}</span>
                        </div>
                      ) : (
                        <TruncatedLabelWithTooltip
                          text={option.label}
                          className="min-w-0 flex-1 text-sm"
                          testId={`${baseTestId}-option-${index}-tooltip`}
                        />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {hasMore && onLoadMore && loadMoreLabel && (
                <CommandGroup>
                  <CommandItem value="load-more" disabled={loadingMore} onSelect={onLoadMore}>
                    {loadMoreLabel}
                  </CommandItem>
                </CommandGroup>
              )}
              {showCreateOption && (
                <CommandGroup>
                  <CommandItem
                    value={`${trimmedSearchValue || 'create-option'} create-option`}
                    onSelect={handleCreateOption}
                    disabled={isCreatingOption || !canCreateOption}
                    data-testid={`${baseTestId}-create-option`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                    {createOptionLabel
                      ? createOptionLabel(trimmedSearchValue)
                      : trimmedSearchValue
                        ? `Agregar nuevo "${trimmedSearchValue}"`
                        : 'Agregar nuevo'}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

export default Combobox;
